import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { throttle } from "throttle-debounce";
import { Gallery, Mosaic } from "@sunwing/wvi-components";
import { RCL as useTranslation } from "../../components/RCL";
import { cloudinaryGenURL } from "../../utils/cloudinaryHelpers";
import {
	aspectRatioMapping,
	convertAspectRatioMapping,
} from "../../utils/Images/getAspectRatioMapping";
import { RichTextConnector } from "../RichTextConnector";
import { useBreakpoints } from "../../hooks";
import * as containerStyles from "../../components/Section/Section.module.scss";

export const galleryVariants = {
	default: "default",
	mosaic: "mosaic",
	clickable: "clickable",
};

const GalleryConnector = ({ data, variant, tags, usedInFullWidthContainer = false, ...props }) => {
	const { isXs, isSm, isMd, isLg, isXl } = useBreakpoints();

	const isMosaic = (data?.variant?.toLowerCase() || variant) === galleryVariants.mosaic;
	let aspectRatioValue;

	const galleryRef = useRef(null);
	const [galleryColumnProportion, setGalleryColumnProportion] = useState(null);

	useEffect(() => {
		if (typeof window === "undefined") return;

		function findRelevantParent(el) {
			if (!el) return null;

			const priorities = [containerStyles.sectionContent];
			let parent = el.parentElement;

			while (parent) {
				if (priorities.some(token => parent.classList.contains(token))) {
					return parent;
				}
				parent = parent.parentElement;
			}

			return null;
		}

		const child = galleryRef.current;
		// Accessing parentElement works for the current Page Section implementation.
		// This may break if the DOM structure of Section changes in the future.
		const grandParent = findRelevantParent(child);

		if (!grandParent || !child) return;

		const getGalleryWidthCategory = percent => {
			if (usedInFullWidthContainer) return "100%";
			if (percent < 49) return "25%";
			if (percent < 51) return "50%";
			if (percent < 75) return "66%";
			return "100%";
		};

		function updateGalleryColumnProportion() {
			const percent = Math.ceil((child.offsetWidth / grandParent.offsetWidth) * 100);
			setGalleryColumnProportion(getGalleryWidthCategory(percent));
		}

		updateGalleryColumnProportion();

		const handleResize = throttle(150, () => {
			updateGalleryColumnProportion();
		});

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [usedInFullWidthContainer]);

	const breakpoints = {
		desktop: {
			match: isLg || isXl,
			widths: {
				"100%": "10:4",
				"75%": "10:4",
				"66%": "16:9",
				"50%": "16:9",
				"25%": "1:1",
			},
		},
		tablet: {
			match: isSm || isMd,
			widths: {
				"100%": "10:4",
				"50%": "4:3",
				"25%": "4:3",
			},
		},
		mobile: {
			match: isXs,
			widths: {
				"100%": "1:1",
			},
		},
	};

	if (isMosaic) {
		aspectRatioValue = isXs || isSm ? "1:1" : "4:3";
	} else {
		if (galleryColumnProportion) {
			for (const bp of Object.values(breakpoints)) {
				if (bp.match && bp.widths[galleryColumnProportion]) {
					aspectRatioValue = aspectRatioMapping[bp.widths[galleryColumnProportion]];
					break;
				}
			}
		} else {
			aspectRatioValue = data.aspectRatio;
		}
	}

	const aspectRatio = convertAspectRatioMapping(aspectRatioValue)[0];

	const maxWidth = isMosaic ? 600 : 1600;

	const isDesktop = !isXs && !isSm;

	const croppedImages = data.images?.map((image, index) => {
		let currentAspectRatio = aspectRatio;

		if (isMosaic && isDesktop) {
			currentAspectRatio = convertAspectRatioMapping(index === 0 ? "4:3" : "1:1")[0];
		}

		if (isMosaic && (isXs || isSm)) {
			currentAspectRatio = convertAspectRatioMapping("1:1")[0];
		}

		return cloudinaryGenURL({
			cloudinaryImage: image,
			aspectRatio: currentAspectRatio,
			maxWidth,
		});
	});

	const labels = {
		openModalLabel: useTranslation({ searchKey: "vwq-open-gallery-modal-label" }),
		firstSlideMessage: useTranslation({ searchKey: "this-is-the-first-slide" }),
		lastSlideMessage: useTranslation({ searchKey: "this-is-the-last-slide" }),
		prevSlideMessage: useTranslation({ searchKey: "previous-slide" }),
		nextSlideMessage: useTranslation({ searchKey: "next-slide" }),
		paginationBulletMessage: useTranslation({ searchKey: "go-to-slide-index" }),
		slideLabelMessage: useTranslation({ searchKey: "slider-index-of-slide-length" }),
		closeModal: useTranslation({ searchKey: "modal-close" }),
		viewAll: useTranslation({ searchKey: "view-all" }),
	};

	const images = croppedImages.map(image => ({
		alt: "",
		id: image.data.public_id,
		public_id: image.data.public_id,
		source: {
			sizes: image.data.sizes,
			src: image.data.src,
			srcSet: image.data.srcSet,
		},
	}));

	const modalImages = data.images?.map(image =>
		cloudinaryGenURL({ cloudinaryImage: image, aspectRatio: 1.77, maxWidth: 1600 })
	);

	if (isMosaic) {
		return (
			<Mosaic
				images={images.slice(0, 5)}
				labels={{
					openModalLabel: labels.viewAll,
				}}
				modalImages={modalImages}
				tags={tags}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}
			/>
		);
	}

	const galleryTags = data?.tags
		?.map(tag => {
			if (!tag) {
				return false;
			}

			return {
				content: tag?.content ? <RichTextConnector data={tag?.content} /> : "",
				icon: tag?.icon,
				label: tag?.label,
			};
		})
		.filter(x => x);

	return (
		<div ref={galleryRef}>
			<Gallery
				images={croppedImages}
				modalImages={modalImages}
				labels={labels}
				aspectRatio={data.aspectRatio}
				variant={data?.variant?.toLowerCase() || variant}
				tags={galleryTags}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}
			/>
		</div>
	);
};

GalleryConnector.propTypes = {
	data: PropTypes.shape({
		images: PropTypes.arrayOf(
			PropTypes.shape({
				public_id: PropTypes.string,
			})
		),
		variant: PropTypes.oneOf(Object.values(galleryVariants)),
		aspectRatio: PropTypes.oneOf(["16:5", "16:9"]),
		tags: PropTypes.arrayOf(PropTypes.object),
	}).isRequired,
	variant: PropTypes.oneOf(Object.values(galleryVariants)),
	tags: PropTypes.arrayOf(PropTypes.string),
	usedInFullWidthContainer: PropTypes.bool,
};

export { GalleryConnector };
